/**
 * Telegram Mini App helpers for the Ghost Booking page.
 *
 * Inside the Telegram WebView plain `window.location.href = "https://t.me/…"`
 * is unreliable: Telegram either swallows the navigation or opens yet another
 * in-app browser on top of the Mini App. The documented way out is
 * `WebApp.openTelegramLink()` for t.me deep links and `WebApp.openLink()` for
 * everything else. Outside Telegram both fall back to a normal navigation.
 *
 * Shape of the global mirrors modules/shell/KnopkaTelegramStartParamGuard.tsx —
 * we deliberately re-declare the few methods we use instead of pulling in the
 * full telegram-web-app typings, which cal.diy does not ship.
 */

export type GhostTelegramWebApp = {
  initData?: string;
  initDataUnsafe?: {
    start_param?: string;
    user?: { id?: number | string; first_name?: string; last_name?: string };
  };
  /** "light" | "dark" — the client's active color scheme. */
  colorScheme?: string;
  ready?: () => void;
  expand?: () => void;
  close?: () => void;
  openTelegramLink?: (url: string) => void;
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
  /** Only "themeChanged" is used here; the real SDK supports more event types. */
  onEvent?: (eventType: string, callback: () => void) => void;
  offEvent?: (eventType: string, callback: () => void) => void;
  BackButton?: {
    show?: () => void;
    hide?: () => void;
    onClick?: (callback: () => void) => void;
    offClick?: (callback: () => void) => void;
  };
  HapticFeedback?: {
    impactOccurred?: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
    notificationOccurred?: (type: "error" | "success" | "warning") => void;
  };
};

export function getTelegramWebApp(): GhostTelegramWebApp | null {
  if (typeof window === "undefined") return null;
  return (window as Window & { Telegram?: { WebApp?: GhostTelegramWebApp } }).Telegram?.WebApp || null;
}

/** True only inside a real Mini App launch (the global alone is not enough). */
export function isTelegramMiniApp(): boolean {
  const tg = getTelegramWebApp();
  if (!tg) return false;
  return Boolean(tg.initData || tg.initDataUnsafe?.user || tg.initDataUnsafe?.start_param);
}

/** Best-effort haptics — absent outside Telegram and on some desktop clients. */
export function ghostHaptic(kind: "select" | "success"): void {
  const tg = getTelegramWebApp();
  if (!tg?.HapticFeedback) return;
  try {
    if (kind === "success") tg.HapticFeedback.notificationOccurred?.("success");
    else tg.HapticFeedback.impactOccurred?.("light");
  } catch {
    // Haptics are decoration; never let them break the flow.
  }
}

/** `https://t.me/<username>?text=…` — must go through openTelegramLink in a Mini App. */
export function openTelegramDeepLink(url: string): void {
  const tg = getTelegramWebApp();
  if (isTelegramMiniApp() && typeof tg?.openTelegramLink === "function") {
    try {
      tg.openTelegramLink(url);
      return;
    } catch {
      // fall through to a normal navigation
    }
  }
  window.location.href = url;
}

/**
 * Anything that is not a t.me link. `openLink` only understands http(s), so
 * mailto: keeps the plain navigation path even inside Telegram.
 */
export function openExternalUrl(url: string): void {
  const tg = getTelegramWebApp();
  const isHttp = /^https?:\/\//i.test(url);
  if (isHttp && isTelegramMiniApp() && typeof tg?.openLink === "function") {
    try {
      tg.openLink(url);
      return;
    } catch {
      // fall through
    }
  }
  window.location.href = url;
}

/**
 * Page title. In the Mini App the header shows document.title, so leaving
 * cal.diy's "404: Страница не найдена." there makes a working booking page
 * look broken. Called once the profile is known.
 */
export function setGhostDocumentTitle(title: string): void {
  if (typeof document === "undefined") return;
  const clean = (title || "").trim();
  if (!clean) return;
  document.title = clean;
}

/**
 * `WebApp.ready()` lets Telegram drop the splash screen sooner; `expand()`
 * opens the Mini App to full height instead of the half-height sheet, which
 * would otherwise force scrolling to see the slot grid. Call once on mount,
 * only inside a real Mini App launch — both are no-ops (and may simply not
 * exist) on older clients, hence the try/catch per call.
 */
export function prepareTelegramViewport(): void {
  const tg = getTelegramWebApp();
  if (!tg || !isTelegramMiniApp()) return;
  try {
    tg.ready?.();
  } catch {
    // old client — ignore
  }
  try {
    tg.expand?.();
  } catch {
    // old client — ignore
  }
}

/**
 * Wires `WebApp.BackButton` to `onBack` and shows it. Returns a cleanup
 * function — call it from the effect's cleanup (component unmount, or a
 * dependency change) or the click handler and the visible button both
 * outlive the page. No-op outside a real Mini App launch, or on a client
 * old enough not to expose BackButton at all.
 */
export function subscribeGhostTelegramBackButton(onBack: () => void): () => void {
  const tg = getTelegramWebApp();
  const backButton = tg?.BackButton;
  if (!isTelegramMiniApp() || !backButton) return () => {};

  try {
    backButton.show?.();
  } catch {
    // old client — ignore
  }
  try {
    backButton.onClick?.(onBack);
  } catch {
    // old client — ignore
  }

  return () => {
    try {
      backButton.offClick?.(onBack);
    } catch {
      // ignore
    }
    try {
      backButton.hide?.();
    } catch {
      // ignore
    }
  };
}

/** `WebApp.close()` — used by the back-button handler when there's nowhere to go back to. */
export function closeTelegramMiniApp(): void {
  const tg = getTelegramWebApp();
  if (!tg) return;
  try {
    tg.close?.();
  } catch {
    // old client — ignore
  }
}

export type GhostColorScheme = "light" | "dark";

/** `WebApp.colorScheme`, narrowed to the two known values (null otherwise/outside Telegram). */
export function ghostTelegramColorScheme(): GhostColorScheme | null {
  const tg = getTelegramWebApp();
  const scheme = tg?.colorScheme;
  return scheme === "dark" || scheme === "light" ? scheme : null;
}

/**
 * Subscribes to the client's live theme switch so a day/night change doesn't
 * need a page reload. Returns an unsubscribe function — call it from the
 * effect's cleanup, same reasoning as subscribeGhostTelegramBackButton above:
 * Telegram does not remove the listener on its own.
 */
export function subscribeGhostTelegramTheme(onChange: (scheme: GhostColorScheme | null) => void): () => void {
  const tg = getTelegramWebApp();
  if (!tg || typeof tg.onEvent !== "function") return () => {};
  const handler = () => onChange(ghostTelegramColorScheme());
  try {
    tg.onEvent("themeChanged", handler);
  } catch {
    return () => {};
  }
  return () => {
    try {
      tg.offEvent?.("themeChanged", handler);
    } catch {
      // ignore
    }
  };
}

/**
 * `initDataUnsafe.user.first_name` (+ `last_name` when present) — a default
 * value only. Empty outside a real Mini App launch, or when Telegram didn't
 * hand over a user object at all.
 */
export function ghostTelegramVisitorName(): string {
  if (!isTelegramMiniApp()) return "";
  const tg = getTelegramWebApp();
  const user = tg?.initDataUnsafe?.user;
  const first = (user?.first_name || "").trim();
  const last = (user?.last_name || "").trim();
  return [first, last].filter(Boolean).join(" ");
}
